import { Button } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationOptions } from 'react-query';
import * as Yup from 'yup';

import { merchantsApi } from 'api';
import {
  DataWrapper,
  FormActions,
  FormControls,
  FormikNumericField,
} from 'components';
import { QueryKey } from 'enums';
import { useCurrencies, useMutation, usePartialQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Merchant, User } from 'types';
import { validationUtils } from 'utils';

type Props = {
  user: User;
};

type UserMerchantSettingsFormValues = {
  depositAmount: number;
};

export const UserMerchantSettingsForm: React.FC<Props> = ({ user }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.user_details.merchant_user',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const { role } = useUser();

  const { defaultAssetCurrency, getAssetCurrencySymbol } = useCurrencies();

  const schema = useMemo(
    () =>
      Yup.object().shape({
        depositAmount: Yup.number().min(
          0,
          tCommon('errors.natural_or_zero_number'),
        ),
      }),
    [tCommon],
  );

  const [initialValues, setInitialValues] =
    useState<UserMerchantSettingsFormValues>({
      depositAmount: 0,
    });

  const queryResultMerchant = usePartialQuery(
    [QueryKey.Merchants, user.id],
    () => merchantsApi.getOneByUserId({ userId: user.id }),
    {
      onSuccess: (data) => {
        setInitialValues({
          depositAmount: data.depositAmount || 0,
        });
      },
    },
  );

  const { mutate: updateUserMerchant } = useMutation<
    Merchant,
    AxiosError,
    { id: string; data: UserMerchantSettingsFormValues },
    unknown
  >(merchantsApi.updateAsRole(role));

  const handleUpdateMerchant = useCallback(
    (
      data: UserMerchantSettingsFormValues,
      options:
        | MutationOptions<Merchant, AxiosError, Partial<Merchant>, unknown>
        | undefined,
    ) => {
      updateUserMerchant(
        { id: queryResultMerchant.data?.id || '', data },
        options,
      );
    },
    [updateUserMerchant, queryResultMerchant.data?.id],
  );

  const handleSubmit = useCallback(
    (
      values: UserMerchantSettingsFormValues,
      {
        setSubmitting,
        setErrors,
        resetForm,
      }: FormikHelpers<UserMerchantSettingsFormValues>,
    ) => {
      handleUpdateMerchant(values, {
        onSettled: () => {
          setSubmitting(false);
        },
        onError: (error: AxiosError) => {
          setErrors(validationUtils.getFormErrors(error));
        },
        onSuccess: (data: Merchant) => {
          setInitialValues({
            depositAmount: data.depositAmount || 0,
          });
          resetForm();
        },
      });
    },
    [handleUpdateMerchant],
  );

  return (
    <Fragment>
      <div className="tw-text-xl tw-mb-8">{t('title')}</div>
      <DataWrapper queryResult={queryResultMerchant}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, isValid }) => (
            <Form className="tw-max-w-xs">
              <FormControls>
                <FormikNumericField
                  name="depositAmount"
                  label={t('fields.deposit_amount')}
                  allowNegative={false}
                  defaultValue={0}
                  suffix={getAssetCurrencySymbol(defaultAssetCurrency?.id)}
                  helperText={t('description.deposit_amount')}
                />
              </FormControls>

              <FormActions>
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={isSubmitting || !isValid}
                >
                  {tCommon('buttons.save')}
                </Button>
              </FormActions>
            </Form>
          )}
        </Formik>
      </DataWrapper>
    </Fragment>
  );
};
